import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import "../../../styles/orders.css";
import { useMediaQuery } from "react-responsive";
import io from "socket.io-client";
import VerifiedNavigation from "../../navigation/components/VerifiedNavigation";
import { MdMoreVert } from "react-icons/md";
import { OrderServices } from "../services/OrderServices";

const READY_TIME_OPTIONS = [
  5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60, 65, 70, 75, 80, 85, 90,
];

const OrderModal = () => {
  const { orderNumber } = useParams();

  const [order, setOrder] = useState(null);
  const [orderStatus, setOrderStatus] = useState(null);
  const [searchStatus, setSearchStatus] = useState(null);
  const [orderRejected, setOrderRejected] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [orderCanceledReason, setOrderCanceledReason] = useState("");
  const [prepTime, setPrepTime] = useState(30);
  const isMobile = useMediaQuery({ query: "(max-width: 768px)" });
  const socketRef = useRef();

  const fetchOrders = async () => {
    const response = await OrderServices.fetchOrders();
    const matched = response.data.find((o) => o.orderNumber === orderNumber);

    if (matched) {
      setOrder(matched);
      setOrderStatus(matched.orderStatus);

      if (matched.orderStatus === "readyForPickup") {
        setSearchStatus("searching");
      }

      if (matched.orderStatus === "driverFound") {
        setSearchStatus("driverFound");
      }

      if (matched.orderStatus === "arrivingAtPickup") {
        setSearchStatus("arrivingAtPickup");
      }

      if (matched.orderStatus === "outForDelivery") {
        setSearchStatus("outForDelivery");
      }

      if (matched.orderStatus === "canceled") {
        setOrderCanceledReason(matched.rejectionReason);
      }
    }
  };

  useEffect(() => {
    socketRef.current = io(process.env.REACT_APP_WEBSOCKET_URL, {
      transports: ["websocket"],
      withCredentials: true,
    });
  }, []);

  useEffect(() => {
    if (!socketRef.current) return;

    const handleSearchStatus = ({ status }) => {
      setSearchStatus(status);
    };

    socketRef.current.on("order-search-status", handleSearchStatus);
    return () =>
      socketRef.current.off("order-search-status", handleSearchStatus);
  }, []);

  const handleAcceptOrder = async () => {
    const response = await OrderServices.updateOrderStatus({
      order,
      status: "confirmed",
    });

    const newStatus =
      response.orderStatus || response.orderFound?.orderStatus || "confirmed";

    setOrderStatus(newStatus);

    socketRef.current.emit("orderConfirmed", {
      order,
      orderNumber,
      orderStatus: newStatus,
    });
  };

  const handleReadyForPickUp = async () => {
    setSearchStatus("searching");

    const response = await OrderServices.updateOrderStatus({
      order,
      status: "readyForPickup",
    });

    const newStatus =
      response.orderStatus ||
      response.orderFound?.orderStatus ||
      "readyForPickup";

    setOrderStatus(newStatus);
  };

  const handleRejectSubmit = async () => {
    if (!rejectReason) return;

    const response = await OrderServices.rejectOrder(orderNumber, rejectReason);

    setOrderStatus("canceled");
    setOrderCanceledReason(rejectReason);
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  if (!order) return null;

  const renderActions = () => {
    if (orderStatus === "canceled") return null;

    if (orderRejected) {
      return (
        <>
          <div className="reject-options">
            <label>Select a reason:</label>
            <select
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
            >
              <option value="">-- Select a reason --</option>
              <option value="Item(s) out of stock">Item(s) out of stock</option>
              <option value="Closed for the day">Closed for the day</option>
              <option value="Bad customer history">Bad customer history</option>
            </select>
          </div>
          <button onClick={handleRejectSubmit} className="reject">
            Submit
          </button>
        </>
      );
    }

    // Main Status Flow
    switch (orderStatus) {
      case "pending":
        return (
          <>
            <button onClick={() => setOrderRejected(true)} className="reject">
              Decline
            </button>
            <button onClick={handleAcceptOrder} className="accept">
              Accept
            </button>
          </>
        );

      case "confirmed":
        return (
          <button
            onClick={handleReadyForPickUp}
            className={
              searchStatus === "searching" ? "search-btn-active" : "accept"
            }
            disabled={searchStatus === "searching"}
          >
            {searchStatus === "searching" ? "Searching…" : "Ready for pickup?"}
          </button>
        );

      default:
        return null;
    }
  };

  return (
    <div className="order-modal">
      <VerifiedNavigation />

      <div className="order-modal-container">
        {searchStatus === "searching" && (
          <div className="searching-banner">
            <p>
              🔎 We’re searching for a driver… Refresh the page in a few
              minutes.
            </p>
          </div>
        )}

        {searchStatus === "driverFound" && (
          <div className="searching-banner">
            <p>
              ✅ A driver has been found for this order! You will be notified
              when they are approaching. Refresh the page in a few minutes.
            </p>
          </div>
        )}

        {searchStatus === "arrivingAtPickup" && (
          <div className="searching-banner">
            <p>
              🚗 Your driver is arriving. Please verify the ORDER NUMBER with
              the driver before handing off!
            </p>
          </div>
        )}

        {searchStatus === "outForDelivery" && (
          <div className="searching-banner">
            <p>
              🚚 The driver is out for delivery. You will be notified once the
              package has reached the customer.
            </p>
          </div>
        )}

        {searchStatus === "search_failed" && (
          <div className="searching-banner failed">
            <p>⚠️ No drivers are available right now.</p>
          </div>
        )}

        <div className="order-flex">
          <div className="order">
            <div className="order-name">
              <div>
                <h1>{order.customer.name}</h1>
                <p style={{ color: "black", fontWeight: 700 }}>
                  {order.orderNumber}
                </p>
                <p>{order.items.length} item(s)</p>
              </div>
            </div>

            <div className="order-details">
              {order.items.map((item) => (
                <div className="order-item" key={item._id}>
                  <div className="order-item-right">
                    <img src={item.image} alt="" />
                    <p>
                      {item.quantity}x. {item.name}(s)
                    </p>
                  </div>

                  <p>
                    ${item.price} <MdMoreVert size={17} />
                  </p>
                </div>
              ))}
            </div>

            <div className="order-total-container">
              <p>
                Total: <span>${order.totalAmount}</span>
              </p>
            </div>

            {/* ACTION BUTTONS */}
            <div className="order-options">{renderActions()}</div>

            {/* CANCELED MESSAGE */}
            {orderStatus === "canceled" && (
              <div className="canceled-message">
                <h2>This order has been canceled</h2>
                <p>
                  Reason:{" "}
                  <span>{orderCanceledReason || order.rejectedReason}</span>
                </p>
              </div>
            )}
          </div>

          {/* PREP TIME */}
          <div className="order-prep-time">
            <h3>Ready Time</h3>
            <h2>{prepTime} mins</h2>
            <p>Suggested</p>

            <select
              value={prepTime}
              onChange={(e) => setPrepTime(e.target.value)}
            >
              <option value="" disabled>
                Set Ready Time
              </option>
              {READY_TIME_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {opt} mins
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderModal;
